import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useGenerateStrategy } from '@/hooks/useCampaignPlanner';
import { ChevronLeft, ChevronRight, RefreshCw, Sparkles, Target, Gift, Calendar } from 'lucide-react';
import { toast } from 'sonner';

interface PlannerStep2BriefProps {
  idea: string;
  goal: string;
  companyId: string;
  onBriefGenerated: (brief: any) => void;
  onBack: () => void;
  existingBrief?: any;
}

export const PlannerStep2Brief: React.FC<PlannerStep2BriefProps> = ({ idea, goal, companyId, onBriefGenerated, onBack, existingBrief }) => {
  const generateStrategy = useGenerateStrategy();
  const [brief, setBrief] = useState<any>(existingBrief || null);

  const generate = async () => {
    try {
      const result = await generateStrategy.mutateAsync({ idea, goal, companyId });
      setBrief(result.brief);
    } catch (e: any) {
      toast.error('Failed to generate strategy: ' + (e.message || 'Unknown error'));
    }
  };

  useEffect(() => {
    if (!brief) generate();
  }, []);

  if (generateStrategy.isPending) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <Sparkles className="h-10 w-10 text-primary animate-pulse" />
        <p className="text-sm text-muted-foreground">AI is crafting your strategic brief...</p>
      </div>
    );
  }

  if (!brief) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <p className="text-sm text-muted-foreground">No brief generated yet.</p>
        <Button onClick={generate}>Try Again</Button>
      </div>
    );
  }

  const sections = [
    { icon: Sparkles, title: 'The Hook', content: brief.hook, color: 'text-amber-500' },
    { icon: Target, title: 'Target Segments', content: Array.isArray(brief.target_segments) ? brief.target_segments.join(', ') : brief.target_segments, color: 'text-blue-500' },
    { icon: Gift, title: 'Core Offer', content: brief.core_offer, color: 'text-green-500' },
    { icon: Calendar, title: 'Timeline', content: brief.timeline, color: 'text-purple-500' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sections.map((s) => (
          <Card key={s.title}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <s.icon className={`h-4 w-4 ${s.color}`} />
                {s.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{s.content || 'N/A'}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={generate} disabled={generateStrategy.isPending} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Regenerate
          </Button>
          <Button onClick={() => onBriefGenerated(brief)} className="gap-2">
            Generate Assets
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
